import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { FileText, ClipboardList, Brain, Briefcase, Trophy, AlertCircle } from 'lucide-react';
import { contentApi, surveyApi } from '@/services/adminApi';
import { skillApi, occupationApi, painPointApi, winApi } from '@/services/skillKnowledgeApi';
import { useToast } from '@/hooks/useToast';

interface DashboardStats {
  totalContents: number;
  totalSurveys: number;
  publishedContents: number;
  publishedSurveys: number;
  totalSkills: number;
  totalOccupations: number;
  totalWins: number;
  totalPainPoints: number;
}

export const AdminDashboardPage: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalContents: 0,
    totalSurveys: 0,
    publishedContents: 0,
    publishedSurveys: 0,
    totalSkills: 0,
    totalOccupations: 0,
    totalWins: 0,
    totalPainPoints: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const abortController = new AbortController();
    
    const fetchStats = async () => {
      try {
        setIsLoading(true);
        const [
          contents,
          surveys,
          publishedContents,
          publishedSurveys,
          skills,
          occupations,
          wins,
          painPoints,
        ] = await Promise.all([
          contentApi.list({ page: 0, size: 1 }).catch(() => ({ content: [], page: { totalElements: 0, totalPages: 0, size: 0, number: 0 } })),
          surveyApi.list({ page: 0, size: 1 }).catch(() => ({ content: [], page: { totalElements: 0, totalPages: 0, size: 0, number: 0 } })),
          contentApi.list({ page: 0, size: 1, status: 'PUBLISHED' }).catch(() => ({ content: [], page: { totalElements: 0, totalPages: 0, size: 0, number: 0 } })),
          surveyApi.list({ page: 0, size: 1, status: 'PUBLISHED' }).catch(() => ({ content: [], page: { totalElements: 0, totalPages: 0, size: 0, number: 0 } })),
          skillApi.list({ page: 0, size: 1 }).catch(() => ({ content: [], page: { totalElements: 0, totalPages: 0, size: 0, number: 0 } })),
          occupationApi.list({ page: 0, size: 1 }).catch(() => ({ content: [], page: { totalElements: 0, totalPages: 0, size: 0, number: 0 } })),
          winApi.list({ page: 0, size: 1 }).catch(() => ({ content: [], page: { totalElements: 0, totalPages: 0, size: 0, number: 0 } })),
          painPointApi.list({ page: 0, size: 1 }).catch(() => ({ content: [], page: { totalElements: 0, totalPages: 0, size: 0, number: 0 } })),
        ]);

        // Only update state if component is still mounted
        if (!abortController.signal.aborted) {
          setStats({
            totalContents: contents.page?.totalElements || 0,
            totalSurveys: surveys.page?.totalElements || 0,
            publishedContents: publishedContents.page?.totalElements || 0,
            publishedSurveys: publishedSurveys.page?.totalElements || 0,
            totalSkills: skills.page?.totalElements || 0,
            totalOccupations: occupations.page?.totalElements || 0,
            totalWins: wins.page?.totalElements || 0,
            totalPainPoints: painPoints.page?.totalElements || 0,
          });
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error('Error fetching dashboard stats:', error);
          toast({
            variant: 'destructive',
            title: 'Error',
            description: 'Failed to load dashboard statistics',
          });
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchStats();

    // Cleanup function
    return () => {
      abortController.abort();
    };
  }, [toast]);

  // Memoize statCards to prevent unnecessary re-renders
  const statCards = useMemo(() => [
    {
      title: 'Total Contents',
      value: stats.totalContents,
      description: `${stats.publishedContents} published`,
      icon: <FileText className="h-8 w-8" />,
      link: '/admin/contents',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50 dark:bg-blue-950',
    },
    {
      title: 'Total Surveys',
      value: stats.totalSurveys,
      description: `${stats.publishedSurveys} published`,
      icon: <ClipboardList className="h-8 w-8" />,
      link: '/admin/surveys',
      color: 'text-green-600',
      bgColor: 'bg-green-50 dark:bg-green-950',
    },
    {
      title: 'Total Skills',
      value: stats.totalSkills,
      description: 'Skill management',
      icon: <Brain className="h-8 w-8" />,
      link: '/admin/skills',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50 dark:bg-purple-950',
    },
    {
      title: 'Total Occupations',
      value: stats.totalOccupations,
      description: 'Occupation management',
      icon: <Briefcase className="h-8 w-8" />,
      link: '/admin/occupations',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50 dark:bg-orange-950',
    },
    {
      title: 'Total Wins',
      value: stats.totalWins,
      description: 'Win management',
      icon: <Trophy className="h-8 w-8" />,
      link: '/admin/wins',
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50 dark:bg-indigo-950',
    },
    {
      title: 'Pain Points',
      value: stats.totalPainPoints,
      description: 'Pain point management',
      icon: <AlertCircle className="h-8 w-8" />,
      link: '/admin/pain-points',
      color: 'text-red-600',
      bgColor: 'bg-red-50 dark:bg-red-950',
    },
  ], [stats]);

  // Memoize quickActions to prevent unnecessary re-renders
  const quickActions = useMemo(() => [
    {
      title: 'Create Content',
      description: 'Upload and manage new content',
      icon: <FileText className="h-6 w-6" />,
      link: '/admin/contents/new',
      variant: 'default' as const,
    },
    {
      title: 'Create Survey',
      description: 'Build a new survey',
      icon: <ClipboardList className="h-6 w-6" />,
      link: '/admin/surveys/new',
      variant: 'default' as const,
    },
    {
      title: 'Create Skill',
      description: 'Add a new skill',
      icon: <Brain className="h-6 w-6" />,
      link: '/admin/skills/new',
      variant: 'default' as const,
    },
    {
      title: 'Create Occupation',
      description: 'Add a new occupation',
      icon: <Briefcase className="h-6 w-6" />,
      link: '/admin/occupations/new',
      variant: 'default' as const,
    },
  ], []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground mt-2">
          Welcome to the Five AI Admin Panel
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {statCards.map((stat) => (
          <Card key={stat.title} className={stat.bgColor}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
              <div className={stat.color}>{stat.icon}</div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{isLoading ? '...' : stat.value}</div>
              <p className="text-xs text-muted-foreground mt-1">{stat.description}</p>
              <Button variant="link" className="p-0 h-auto mt-2" asChild>
                <Link to={stat.link}>View all →</Link>
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {quickActions.map((action) => (
            <Card key={action.title} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-center gap-3">
                  {action.icon}
                  <CardTitle className="text-lg">{action.title}</CardTitle>
                </div>
                <CardDescription>{action.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <Button variant={action.variant} asChild>
                  <Link to={action.link}>Get Started</Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Recent Activity Placeholder */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Recent Activity</h2>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground text-center py-8">
              Recent activity will be displayed here
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboardPage;


